/** A z-order command, named after the ShapeManager method that performs it. */
export type CanvasArrangeCommand = 'bringToFront' | 'bringForward' | 'sendBackward' | 'sendToBack'

/** What a canvas keydown asks for — the first branch of the shortcut table it matches. */
export type CanvasKeyAction =
  | { kind: 'deactivatePanMode' }
  | { kind: 'holdMarqueeZoom' }
  | { kind: 'selectNone' }
  | { kind: 'selectAll' }
  | { kind: 'lockSelection' }
  | { kind: 'toggleFreeze' }
  | { kind: 'copy' }
  | { kind: 'cut' }
  | { kind: 'paste' }
  | { kind: 'duplicate' }
  | { kind: 'suppressFind' }
  | { kind: 'group' }
  | { kind: 'exitAllIsolation' }
  | { kind: 'activateTextTool' }
  | { kind: 'delete' }
  | { kind: 'panByArrow' }
  | { kind: 'nudgeGuides' }
  /**
   * Font-size step for a single selected text frame. It can turn out not to apply (no such
   * frame), and the keystroke then goes on to `fallback` — the next branch an unmodified Up/Down
   * matches.
   */
  | { kind: 'fontSizeStep'; fallback: 'nudgeShapes' | null }
  | { kind: 'nudgeShapes' }
  | { kind: 'arrange'; command: CanvasArrangeCommand }
  | { kind: 'exitNodeEdit' }
  | { kind: 'escape' }

/** The keydown and canvas state `resolveCanvasKeyAction` decides on. */
export type CanvasKeyInput = {
  key: string
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
  shiftKey: boolean
  /** The event's target is an editable surface (`isEditableTarget`). */
  isEditable: boolean
  isPanModeActive: boolean
  isPanInteractionActive: boolean
  hasActiveIsolationGroup: boolean
  hasSelectedGuides: boolean
  canSelectShapes: boolean
  isInShapeCreationMode: boolean
  isNodeEditMode: boolean
}

function isArrowKey(key: string): boolean {
  return key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight'
}

/** Arrow-key nudge of the selected shapes (a fall-through target of `fontSizeStep`). */
function matchesNudgeShapes(input: CanvasKeyInput): boolean {
  return input.canSelectShapes && !input.isInShapeCreationMode && isArrowKey(input.key)
}

/**
 * The canvas keyboard shortcut table, in priority order: the first matching branch wins. Each
 * action's own guards (editable target, live text edit, empty selection…) run in its handler
 * (`handlers/canvas-key-action-handlers.ts`) — a matched branch owns the keystroke even when its
 * handler then does nothing, except `fontSizeStep` (see its `fallback`).
 */
export function resolveCanvasKeyAction(input: CanvasKeyInput): CanvasKeyAction | null {
  const { key, ctrlKey, metaKey, altKey, shiftKey } = input
  const isMod = ctrlKey || metaKey

  if (input.isPanModeActive && key === 'Escape') return { kind: 'deactivatePanMode' }

  if ((key === 'z' || key === 'Z') && !isMod && !altKey && !shiftKey) {
    return { kind: 'holdMarqueeZoom' }
  }

  if ((key === 'a' || key === 'A') && isMod && shiftKey) return { kind: 'selectNone' }

  if ((key === 'a' || key === 'A') && isMod) return { kind: 'selectAll' }

  if ((key === 'l' || key === 'L') && isMod) return { kind: 'lockSelection' }

  if ((key === 'f' || key === 'F') && isMod && shiftKey) return { kind: 'toggleFreeze' }

  if ((key === 'c' || key === 'C') && isMod && !shiftKey) return { kind: 'copy' }

  if ((key === 'x' || key === 'X') && isMod && !shiftKey) return { kind: 'cut' }

  if ((key === 'v' || key === 'V') && isMod) return { kind: 'paste' }

  if ((key === 'd' || key === 'D') && isMod && !shiftKey) return { kind: 'duplicate' }

  // Swallow the browser's native Find-in-page — freezing now lives solely on
  // Ctrl/Cmd+Shift+F (toggleFreezeSelectedObjects above); plain Ctrl/Cmd+F does nothing.
  if ((key === 'f' || key === 'F') && isMod && !shiftKey && !altKey) return { kind: 'suppressFind' }

  if ((key === 'g' || key === 'G') && isMod) return { kind: 'group' }

  // Group Isolation Mode: exit all levels (avoid Shift+Esc due to browser-reserved shortcuts).
  if (
    input.hasActiveIsolationGroup &&
    key === 'Backspace' &&
    shiftKey &&
    !ctrlKey &&
    !metaKey &&
    !altKey
  ) {
    return { kind: 'exitAllIsolation' }
  }

  // T key: activate text tool
  if (key === 't' || key === 'T') return { kind: 'activateTextTool' }

  // Delete / Backspace: delete selected guides first, then all selected objects
  if (key === 'Delete' || key === 'Backspace') return { kind: 'delete' }

  if (input.isPanInteractionActive && !altKey && isArrowKey(key)) return { kind: 'panByArrow' }

  // Arrow keys: nudge selected guides (H guides ↑↓, V guides ←→)
  if (input.hasSelectedGuides && isArrowKey(key)) return { kind: 'nudgeGuides' }

  // Font size step: Up/Down + one text box selected + no shapes, any tool, not in edit mode.
  if (
    !input.isEditable &&
    !ctrlKey &&
    !metaKey &&
    !altKey &&
    !shiftKey &&
    (key === 'ArrowUp' || key === 'ArrowDown')
  ) {
    return { kind: 'fontSizeStep', fallback: matchesNudgeShapes(input) ? 'nudgeShapes' : null }
  }

  // Arrow keys: nudge selected shapes and/or text boxes
  if (matchesNudgeShapes(input)) return { kind: 'nudgeShapes' }

  // Bring to Front: ] (without modifier)
  if (key === ']' && !isMod) return { kind: 'arrange', command: 'bringToFront' }

  // Send to Back: [ (without modifier)
  if (key === '[' && !isMod) return { kind: 'arrange', command: 'sendToBack' }

  // Bring Forward: Cmd+] (Mac) / Ctrl+] (Windows)
  if (key === ']' && isMod) return { kind: 'arrange', command: 'bringForward' }

  // Send Backward: Cmd+[ (Mac) / Ctrl+[ (Windows)
  if (key === '[' && isMod) return { kind: 'arrange', command: 'sendBackward' }

  // Enter exits node-edit mode the same way Escape does: keeps the shape selected.
  if (key === 'Enter' && input.isNodeEditMode) return { kind: 'exitNodeEdit' }

  if (key === 'Escape') return { kind: 'escape' }

  return null
}
